import React from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { UserProvider } from './contexts/UserContext';
import { CandidateSkillsProvider } from './contexts/CandidateSkillsContext';
import { CompanyProvider } from './contexts/CompanyContext';

// Import pages
import HomePage from './app/page';
import LoginPage from './app/login/page';
import RegisterPage from './app/register/page';
import CompanyRegisterPage from './app/company-register/page';
import DashboardPage from './app/dashboard/page';
import InterviewPage from './app/interview/page';
import ProfilePage from './app/profile/page';
import JobsPage from './app/jobs/page';
import CheckoutPage from './app/checkout/page';
import PricingPage from './app/pricing/page';
import SubscriptionPage from './app/subscription/page';
import TransactionsPage from './app/transactions/page';
import TeamsPage from './app/teams/page';
import TestTeamPage from './app/test-team/page';

// Recruiter pages
import RecruiterLayout from './app/recruiter/layout';
import RecruiterDashboard from './app/recruiter/dashboard/page';
import RecruiterCandidates from './app/recruiter/candidates/page';
import CandidateDetailPage from './app/recruiter/candidates/CandidateDetailPage';
import RecruiterPositions from './app/recruiter/positions/page';
import RecruiterActivityPage from './app/recruiter/activity/page';
import RecruiterCreditsPage from './app/recruiter/credits/page';
import PositionDetailPage from './app/recruiter/positions/[id]/ClientPage';
import RecruiterInterviews from './app/recruiter/interviews/page';
import InterviewDetailPage from './app/recruiter/interviews/[id]/ClientPage';
import InterviewEditPage from './app/recruiter/interviews/[id]/edit/ClientPage';
import InterviewSchedulePage from './app/recruiter/interviews/schedule/page';
import RecruiterCodingChallenges from './app/recruiter/coding-challenges/page';
import RecruiterMessages from './app/recruiter/messages/page';
import RecruiterSettings from './app/recruiter/settings/page';
import RecruiterTeams from './app/recruiter/teams/page';
import RecruiterTeamDetail from './app/recruiter/teams/[id]/ClientPage';
import RecruiterTeamManage from './app/recruiter/teams/[id]/manage/page';
import CandidateCompleteDetailPage from './app/recruiter/candidate-position-details/[candidatePositionId]/ClientPage';
import AdminPositionReviewPage from './app/admin/positions/[id]/review/page';
import AdminConsolePage from './app/admin/page';

// Candidate pages
import CandidateLayout from './app/candidate/layout';
import CandidateDashboard from './app/candidate/dashboard/page';
import CandidateProfile from './app/candidate/profile/page';
import CandidateResume from './app/candidate/resume/page';
import CandidateApplications from './app/candidate/applications/page';
import CandidateCareerProfile from './app/candidate/career-profile/page';
import CandidateResumeBuilder from './app/candidate/resume-builder/page';

// Job detail
import JobDetailPage from './app/jobs/[id]/JobDetailPage';

// Public profile
import CandidatePublicProfile from './app/public/candidate-profile/[id]/page';

// Position detail wrapper component
function PositionDetailWrapper() {
  const { id } = useParams<{ id: string }>();
  return <PositionDetailPage params={{ id: id || '' }} />;
}

// Interview detail wrapper component
function InterviewDetailWrapper() {
  const { id } = useParams<{ id: string }>();
  return <InterviewDetailPage />;
}

// Interview edit wrapper component
function InterviewEditWrapper() {
  const { id } = useParams<{ id: string }>();
  return <InterviewEditPage />;
}

// Candidate complete detail wrapper component
function CandidateCompleteDetailWrapper() {
  const { candidatePositionId } = useParams<{ candidatePositionId: string }>();
  return <CandidateCompleteDetailPage candidatePositionUuid={candidatePositionId || ''} />;
}

// Team manage wrapper component
function TeamManageWrapper() {
  return <RecruiterTeamManage />;
}

// Team detail wrapper component
function TeamDetailWrapper() {
  return <RecruiterTeamDetail />;
}

function App() {
  return (
    <CompanyProvider>
      <UserProvider>
        <CandidateSkillsProvider>
          <Router>
            <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/company-register" element={<CompanyRegisterPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/jobs/:id" element={<JobDetailPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/admin" element={<AdminConsolePage />} />
          <Route path="/admin/positions/:id/review" element={<AdminPositionReviewPage />} />
          
          {/* Protected routes */}
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/interview" element={<InterviewPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/teams" element={<TeamsPage />} />
          <Route path="/test-team" element={<TestTeamPage />} />

          {/* Recruiter routes */}
          <Route path="/recruiter" element={<RecruiterLayout />}>
            <Route index element={<RecruiterDashboard />} />
            <Route path="dashboard" element={<RecruiterDashboard />} />
            <Route path="candidates" element={<RecruiterCandidates />} />
            <Route path="candidates/:id" element={<CandidateDetailPage />} />
            <Route path="positions" element={<RecruiterPositions />} />
            <Route path="positions/:id" element={<PositionDetailWrapper />} />
            <Route path="activity" element={<RecruiterActivityPage />} />
            <Route path="credits" element={<RecruiterCreditsPage />} />
            <Route path="interviews" element={<RecruiterInterviews />} />
            <Route path="interviews/schedule" element={<InterviewSchedulePage />} />
            <Route path="interviews/:id" element={<InterviewDetailWrapper />} />
            <Route path="interviews/:id/edit" element={<InterviewEditWrapper />} />
            <Route path="coding-challenges" element={<RecruiterCodingChallenges />} />
            <Route path="messages" element={<RecruiterMessages />} />
            <Route path="settings" element={<RecruiterSettings />} />
            <Route path="teams" element={<RecruiterTeams />} />
            <Route path="teams/:id" element={<TeamDetailWrapper />} />
            <Route path="teams/:id/manage" element={<TeamManageWrapper />} />
            <Route path="candidate-position-details/:candidatePositionId" element={<CandidateCompleteDetailWrapper />} />
          </Route>
          
          <Route path="/public/candidate-profile/:id" element={<CandidatePublicProfile />} />
          
          {/* Candidate routes */}
          <Route path="/candidate" element={<CandidateLayout />}>
            <Route path="dashboard" element={<CandidateDashboard />} />
            <Route path="profile" element={<CandidateProfile />} />
            <Route path="resume" element={<CandidateResume />} />
            <Route path="applications" element={<CandidateApplications />} />
            <Route path="career-profile" element={<CandidateCareerProfile />} />
            <Route path="resume-builder" element={<CandidateResumeBuilder />} />
          </Route>
        </Routes>
      </Router>
      </CandidateSkillsProvider>
    </UserProvider>
    </CompanyProvider>
  );
}

export default App;
